/**
 * DraggitRM - Drag and Drop Resource Management Component
 * 
 * A comprehensive resource planning and scheduling tool that allows users to:
 * - Assign resources to projects across multiple days
 * - Drag resources from a pool to project/day cells
 * - Move resources between different projects and days
 * - Navigate through weeks with Monday-Sunday layout
 * - Persist data in browser localStorage
 * 
 * Features:
 * - Manager resources can be assigned to multiple projects on the same day
 * - Non-manager resources are limited to one project per day
 * - No duplicate assignments allowed (same resource in same project on same day)
 * - Smooth drag-and-drop animations using @dnd-kit library
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Day from './components/Day';
import ResourcePool from './components/ResourcePool';
import Resource from './components/Resource';
import Notification from './components/Notification';
import { withFirebase } from '../../Firebase';
import { AuthUserContext, withAuthorization, withEmailVerification } from '../../Session';
import { SERVER_URL } from '../../../DB_Url';
import './index.css';

// Authorization condition - user must be authenticated
const condition = authUser => !!authUser;

/**
 * Generates an array of 7 consecutive days starting from a given date
 * Uses the date constructor to properly handle month/year boundaries
 * 
 * @param {Date} startDate - The starting date (typically a Monday)
 * @returns {Array} Array of day objects with id and date properties
 */
const generate7Days = (startDate) => {
  const days = [];
  
  for (let i = 0; i < 7; i++) {
    // Create a completely new date object for each day to avoid mutation
    const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i);
    const dateStr = date.toISOString().split('T')[0];
    days.push({
      id: `day-${dateStr}`,
      date: dateStr,
    });
  }
  return days;
};

/**
 * Main DraggitRM Component
 * Manages the entire resource planning interface with drag-and-drop functionality
 */
const DraggitRM = ({ firebase }) => {
  // Get the authenticated user from the context
  const authUser = React.useContext(AuthUserContext);
  /**
   * Calculates the Monday of the week containing the given date
   * Ensures the calendar always starts on Monday regardless of current day
   * 
   * @param {Date} date - Any date within the target week
   * @returns {Date} The Monday of that week, normalized to midnight
   */
  const getMonday = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0); // Normalize to midnight to avoid time-based issues
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const diff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days, otherwise calculate offset to Monday
    d.setDate(d.getDate() + diff);
    return d;
  };
  
  /**
   * Gets the Monday of the current week as the initial display date
   * @returns {Date} Monday of the current week
   */
  const getInitialDate = () => {
    return getMonday(new Date());
  };

  /**
   * Loads persisted projects and resources from Firebase RTDB
   * Falls back to default values if no saved data exists or on error
   * This is now handled in useEffect hooks below
   */
  
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /** Current start date (Monday) of the displayed week */
  const [currentStartDate, setCurrentStartDate] = useState(getInitialDate);
  
  /** Array of 7 consecutive days to display */
  const [days, setDays] = useState(() => generate7Days(getInitialDate()));
  
  /** Array of project objects fetched from API (rows in the table) */
  const [projects, setProjects] = useState([]);
  
  /** Array of assigned resource instances (resources placed in project/day cells) */
  const [resources, setResources] = useState([]);
  
  /** Array of base resource templates that remain in the pool */
  const [poolResources, setPoolResources] = useState([]);
  
  /** Loading state for Firebase data */
  const [loading, setLoading] = useState(true);
  
  /** Flag to track if there are unsaved changes in pool resources */
  const [hasUnsavedPoolChanges, setHasUnsavedPoolChanges] = useState(false);
  
  /** Flag to track if there are unsaved changes in assignments */
  const [hasUnsavedAssignmentChanges, setHasUnsavedAssignmentChanges] = useState(false);
  
  /** Currently dragged resource object (for DragOverlay display) */
  const [activeResource, setActiveResource] = useState(null);
  
  /** ID of the resource being dragged (for tracking purposes) */
  const [activeDragId, setActiveDragId] = useState(null);
  
  /** Current notification object {message, type} or null if no notification */
  const [notification, setNotification] = useState(null);

  // ============================================================================
  // DRAG-AND-DROP SENSORS
  // ============================================================================
  
  /**
   * Configure input sensors for drag-and-drop interactions
   * - PointerSensor: Mouse and touch interactions
   * - KeyboardSensor: Keyboard navigation for accessibility
   */
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================================================
  // PERSISTENCE EFFECTS
  // ============================================================================
  
  /**
   * Load resource management data from Firebase when user authentication changes
   */
  useEffect(() => {
    if (!authUser || !firebase) {
      setLoading(false);
      return;
    }

    const loadFirebaseData = async () => {
      try {
        setLoading(true);
        
        const state = await firebase.doGetUserResourceManagementState(authUser.uid);
        
        if (state) {
          // Load pool resources first
          if (state.poolResources && Array.isArray(state.poolResources)) {
            setPoolResources(state.poolResources);
            
            // Load assignments and reconstruct full resource objects from pool
            if (state.resources && Array.isArray(state.resources)) {
              const reconstructedResources = state.resources.map(assignment => {
                // Find the pool resource this assignment references
                const poolResource = state.poolResources.find(pr => pr.id === assignment.baseResourceId);
                
                if (poolResource) {
                  // Reconstruct full resource object from pool resource + assignment
                  return {
                    id: assignment.id,
                    baseResourceId: assignment.baseResourceId,
                    name: poolResource.name,
                    role: poolResource.role,
                    skillLevel: poolResource.skillLevel,
                    isManager: poolResource.isManager,
                    projectId: assignment.projectId,
                    dayId: assignment.dayId,
                  };
                }
                return null;
              }).filter(r => r !== null); // Remove any invalid assignments
              
              setResources(reconstructedResources);
            }
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading Firebase data:', error);
        setLoading(false);
      }
    };

    loadFirebaseData();
  }, [authUser, firebase]);

  // Mark pool resources as having unsaved changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedPoolChanges(true);
    }
  }, [poolResources, loading]);

  // Mark assignments as having unsaved changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedAssignmentChanges(true);
    }
  }, [resources, loading]);

  // Remove the debounced auto-save effect - we'll save individually on each action
  // This prevents conflicts between individual saves and bulk saves

  // ============================================================================
  // FIREBASE INTEGRATION - FETCH PROJECTS FROM API
  // ============================================================================

  /**
   * Fetches the list of projects from the backend API
   * These projects become the rows in the resource planning table
   * Filters projects based on the user's organization memberships
   */
  useEffect(() => {
    if (!authUser || !authUser.roles) {
      setProjects([]);
      return;
    }

    const fetchProjects = async () => {
      try {
        const userOrgs = Object.keys(authUser.roles).filter(key => authUser.roles[key] !== null);
        if (userOrgs.length === 0) {
          setProjects([]);
          return;
        }

        const projectsUrl = new URL(`${SERVER_URL}/api/projects`);
        projectsUrl.searchParams.append('organisations', userOrgs.join(','));
        
        const response = await fetch(projectsUrl);
        if (response.ok) {
          const data = await response.json();
          // Convert project names to project objects with id and title
          const projectObjects = data.map((projectName, index) => ({
            id: `project-${projectName.replace(/\s+/g, '-').toLowerCase()}`,
            title: projectName
          }));
          setProjects(projectObjects);
          console.log('Loaded projects from API:', projectObjects);
        }
      } catch (error) {
        console.error("Failed to fetch projects:", error);
        setProjects([]);
      }
    };

    fetchProjects();
  }, [authUser]);

  // ============================================================================
  // NOTIFICATION HELPERS
  // ============================================================================
  
  /**
   * Displays a notification message to the user
   * @param {string} message - The message to display
   * @param {string} type - Notification type: 'error', 'success', 'warning', or 'info'
   */
  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
  };

  /**
   * Closes the currently displayed notification
   */
  const closeNotification = () => {
    setNotification(null);
  };

  // ============================================================================
  // DRAG-AND-DROP EVENT HANDLERS
  // ============================================================================
  
  /**
   * Handles the start of a drag operation
   * Sets up the active resource for the DragOverlay component
   * 
   * @param {Object} event - DndKit drag start event
   */
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveDragId(active.id);
    
    // Find the resource being dragged (could be from pool or already assigned)
    const resource = resources.find((r) => r.id === active.id) || 
                     poolResources.find((r) => r.id === active.id);
    if (resource) {
      setActiveResource(resource);
    }
  };

  /**
   * Handles drag-over events to provide real-time visual feedback
   * Creates temporary resource instances when dragging from pool for smooth animations
   * Updates resource positions when dragging assigned resources
   * 
   * This function runs continuously during drag operations to maintain
   * consistent visual feedback matching the drag cursor position
   * 
   * @param {Object} event - DndKit drag over event
   */
  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;

    // Check if the dragged item is a base resource from the pool
    const baseResource = poolResources.find((r) => r.id === activeId);
    const isFromPool = !!baseResource;
    
    // Determine the target project and day based on what's being hovered over
    let projectId, dayId;
    
    if (over.data.current?.type === 'project') {
      // Hovering over empty project space
      projectId = over.data.current.projectId;
      dayId = over.data.current.dayId;
    } else if (over.data.current?.type === 'resource') {
      // Hovering over an existing resource - use its project/day location
      const overResource = resources.find((r) => r.id === overId);
      if (overResource && overResource.projectId && overResource.dayId) {
        projectId = overResource.projectId;
        dayId = overResource.dayId;
      }
    }
    
    // CASE 1: Dragging from pool - create temporary instance for smooth animation
    if (isFromPool && projectId && dayId) {
      setResources((items) => {
        const tempId = `temp-${activeId}`;
        const existingTemp = items.find((r) => r.id === tempId);
        
        if (existingTemp) {
          // Update position if target changed (drag moved to different cell)
          if (existingTemp.projectId !== projectId || existingTemp.dayId !== dayId) {
            return items.map((r) =>
              r.id === tempId
                ? { ...r, projectId, dayId }
                : r
            );
          }
          return items; // No change needed
        } else {
          // Create new temporary instance
          return [...items, {
            id: tempId,
            baseResourceId: baseResource.id,
            name: baseResource.name,
            role: baseResource.role,
            skillLevel: baseResource.skillLevel,
            isManager: baseResource.isManager,
            projectId,
            dayId,
            isTemp: true, // Flag for filtering and styling
          }];
        }
      });
      return;
    }
    
    // CASE 2: Dragging from pool but no valid target - remove temporary instance
    if (isFromPool) {
      setResources((items) => items.filter((r) => r.id !== `temp-${activeId}`));
      return;
    }

    // CASE 3: Dragging assigned resource - update its position in real-time
    const activeResource = resources.find((r) => r.id === activeId);
    if (!activeResource) return;

    // Check if dragging back to resource pool (remove assignment)
    if (overId === 'resource-pool') {
      setResources((items) =>
        items.map((resource) =>
          resource.id === activeId
            ? { ...resource, projectId: null, dayId: null }
            : resource
        )
      );
      return;
    }

    // Check if dropping over a project cell
    if (over.data.current?.type === 'project') {
      const projectId = over.data.current.projectId;
      const dayId = over.data.current.dayId;
      
      // Validate: Check if this resource already exists on this day
      const existingOnDay = resources.find(
        (r) => r.baseResourceId === activeResource.baseResourceId && 
               r.dayId === dayId && 
               r.id !== activeId
      );
      
      // Don't show visual feedback if validation would fail
      if (existingOnDay) {
        return;
      }
      
      // Update resource position for smooth animation
      setResources((items) =>
        items.map((resource) =>
          resource.id === activeId
            ? { ...resource, projectId, dayId }
            : resource
        )
      );
      return;
    }

    // Check if dropping over another resource (join same project/day)
    if (over.data.current?.type === 'resource') {
      const overResource = resources.find((r) => r.id === overId);
      if (overResource && overResource.projectId && overResource.dayId) {
        // Validate: Check if this resource already exists on this day
        const existingOnDay = resources.find(
          (r) => r.baseResourceId === activeResource.baseResourceId && 
                 r.dayId === overResource.dayId && 
                 r.id !== activeId
        );
        
        // Don't show visual feedback if validation would fail
        if (existingOnDay) {
          return;
        }
        
        // Update to match the hovered resource's location
        setResources((items) =>
          items.map((resource) =>
            resource.id === activeId
              ? { ...resource, projectId: overResource.projectId, dayId: overResource.dayId }
              : resource
          )
        );
      }
    }
  };

  /**
   * Handles the end of a drag operation
   * Performs validation and finalizes resource placement or movement
   * 
   * Business Rules Enforced:
   * 1. No duplicate resources in the same project on the same day
   * 2. Non-managers can only be in one project per day
   * 3. Managers can be assigned to multiple projects on the same day
   * 
   * @param {Object} event - DndKit drag end event
   */
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    // Clear drag state
    setActiveResource(null);
    setActiveDragId(null);

    const activeId = active.id;
    
    // Check if the dragged item was from the pool
    const baseResource = poolResources.find((r) => r.id === activeId);
    
    // -------------------------------------------------------------------------
    // CASE 1: Dragging from Resource Pool
    // -------------------------------------------------------------------------
    if (baseResource) {
      const tempId = `temp-${activeId}`;
      
      // Handle cancelled drag (dropped outside valid targets)
      if (!over) {
        setResources((items) => items.filter((r) => r.id !== tempId));
        return;
      }
      
      // Get the temporary instance created during drag-over
      const tempInstance = resources.find((r) => r.id === tempId);
      
      if (tempInstance) {
        const projectId = tempInstance.projectId;
        const dayId = tempInstance.dayId;
        
        // VALIDATION: Check if resource already exists on this day
        const existingOnDay = resources.find(
          (r) => r.baseResourceId === baseResource.id && 
                 r.dayId === dayId && 
                 r.id !== tempId
        );
        
        if (existingOnDay) {
          if (existingOnDay.projectId === projectId) {
            // RULE VIOLATION: Duplicate in same project/day
            showNotification(`${baseResource.name} is already assigned to this project on this day.`, 'error');
            setResources((items) => items.filter((r) => r.id !== tempId));
            return;
          } else {
            // RULE VIOLATION: Non-manager in multiple projects on same day
            if (!baseResource.isManager) {
              showNotification(`${baseResource.name} is already assigned to another project on this day. Only managers can be assigned to multiple projects.`, 'error');
              setResources((items) => items.filter((r) => r.id !== tempId));
              return;
            }
            // Manager is allowed in multiple projects - validation passed
          }
        }
        
        // VALIDATION PASSED: Convert temporary instance to permanent
        const newId = `resource-instance-${Date.now()}-${Math.random()}`;
        setResources((items) =>
          items.map((r) =>
            r.id === tempId
              ? {
                  ...r,
                  id: newId,
                  isTemp: undefined,
                }
              : r
          )
        );
      }
      return;
    }
    
    // -------------------------------------------------------------------------
    // CASE 2: Dragging Already Assigned Resource
    // -------------------------------------------------------------------------
    
    // Handle cancelled drag
    if (!over) {
      return;
    }
    
    const overId = over.id;
    const activeResource = resources.find((r) => r.id === activeId);
    if (!activeResource) return;

    // SUB-CASE 2A: Dropped back into resource pool (unassign)
    if (overId === 'resource-pool') {
      setResources((items) => items.filter((resource) => resource.id !== activeId));
      return;
    }

    // SUB-CASE 2B: Dropped on a project cell
    if (over.data.current?.type === 'project') {
      const projectId = over.data.current.projectId;
      const dayId = over.data.current.dayId;
      
      // VALIDATION: Check if resource already exists on this day
      const existingOnDay = resources.find(
        (r) => r.baseResourceId === activeResource.baseResourceId && 
               r.dayId === dayId && 
               r.id !== activeId
      );
      
      if (existingOnDay) {
        if (existingOnDay.projectId === projectId) {
          // RULE VIOLATION: Duplicate in same project/day
          showNotification(`${activeResource.name} is already assigned to this project on this day.`, 'error');
          return;
        } else {
          // RULE VIOLATION: Non-manager in multiple projects on same day
          if (!activeResource.isManager) {
            showNotification(`${activeResource.name} is already assigned to another project on this day. Only managers can be assigned to multiple projects.`, 'error');
            return;
          }
          // Manager is allowed in multiple projects - validation passed
        }
      }
      
      // VALIDATION PASSED: Update resource assignment
      setResources((items) =>
        items.map((resource) =>
          resource.id === activeId
            ? { ...resource, projectId, dayId }
            : resource
        )
      );
      return;
    }

    // SUB-CASE 2C: Dropped on another resource (join that resource's location)
    if (over.data.current?.type === 'resource') {
      const overResource = resources.find((r) => r.id === overId);
      if (overResource && overResource.projectId && overResource.dayId) {
        // VALIDATION: Check if resource already exists on this day
        const existingOnDay = resources.find(
          (r) => r.baseResourceId === activeResource.baseResourceId && 
                 r.dayId === overResource.dayId && 
                 r.id !== activeId
        );
        
        if (existingOnDay) {
          if (existingOnDay.projectId === overResource.projectId) {
            // RULE VIOLATION: Duplicate in same project/day
            showNotification(`${activeResource.name} is already assigned to this project on this day.`, 'error');
            return;
          } else {
            // RULE VIOLATION: Non-manager in multiple projects on same day
            if (!activeResource.isManager) {
              showNotification(`${activeResource.name} is already assigned to another project on this day. Only managers can be assigned to multiple projects.`, 'error');
              return;
            }
            // Manager is allowed in multiple projects - validation passed
          }
        }
        
        // VALIDATION PASSED: Update to match target resource's location
        setResources((items) =>
          items.map((resource) =>
            resource.id === activeId
              ? { ...resource, projectId: overResource.projectId, dayId: overResource.dayId }
              : resource
          )
        );
        return;
      }
    }

    // SUB-CASE 2D: Dropped in invalid location - remove assignment
    setResources((items) => items.filter((resource) => resource.id !== activeId));
  };

  // ============================================================================
  // PROJECT AND RESOURCE MANAGEMENT
  // ============================================================================
  
  /**
   * Adds a new project column to the table
   * @param {string} dayId - Not used in current implementation
   * @param {string} title - Name of the new project
   */
  const handleAddProject = (dayId, title) => {
    const newProject = {
      id: `project-${Date.now()}`,
      title,
    };
    setProjects((prev) => [...prev, newProject]);
  };

  /**
   * Adds a new base resource to the pool
   * Creates a new resource template that can be cloned and assigned to projects
   * 
   * @param {string} name - Resource name
   * @param {string} role - Resource role/title
   * @param {string} skillLevel - Experience level (Junior, Mid, Senior)
   * @param {boolean} isManager - Whether resource can be in multiple projects
   */
  const handleAddResource = async (name, role, skillLevel, isManager = false) => {
    if (!name || !role || !skillLevel) {
      showNotification('Please provide name, role, and skill level.', 'error');
      return;
    }

    const newResource = {
      id: `base-resource-${Date.now()}`,
      name,
      role,
      skillLevel,
      isManager,
    };
    
    setPoolResources((prev) => [...prev, newResource]);
    showNotification(`${name} has been added to the resource pool. Click "Save to Firebase" to persist.`, 'success');
  };

  /**
   * Updates an existing base resource in the pool
   * 
   * @param {string} id - Resource ID to update
   * @param {Object} updates - Object containing fields to update
   */
  const handleUpdateResource = async (id, updates) => {
    setPoolResources((prev) =>
      prev.map((resource) =>
        resource.id === id ? { ...resource, ...updates } : resource
      )
    );
    
    // Also update all assigned instances of this resource
    setResources((prev) =>
      prev.map((resource) =>
        resource.baseResourceId === id
          ? { ...resource, ...updates }
          : resource
      )
    );
    
    showNotification('Resource has been updated. Click "Save to Firebase" to persist.', 'success');
  };

  /**
   * Removes a base resource from the pool
   * Also removes all assigned instances of this resource
   * 
   * @param {string} id - Resource ID to remove
   */
  const handleDeleteResource = async (id) => {
    const resource = poolResources.find(r => r.id === id);
    if (!resource) return;
    
    if (window.confirm(`Are you sure you want to delete ${resource.name}? This will remove all assignments of this resource.`)) {
      setPoolResources((prev) => prev.filter((r) => r.id !== id));
      setResources((prev) => prev.filter((r) => r.baseResourceId !== id));
      showNotification(`${resource.name} has been removed. Click "Save to Firebase" to persist.`, 'success');
    }
  };

  /**
   * Updates custom parameters for a base resource
   * Also propagates to all assigned instances
   * 
   * @param {string} id - Resource ID
   * @param {Object} params - Updated parameters object
   */
  const handleUpdateResourceParams = (id, params) => {
    setPoolResources((prev) =>
      prev.map((resource) =>
        resource.id === id ? { ...resource, params } : resource
      )
    );
    
    // Also update all assigned instances
    setResources((prev) =>
      prev.map((resource) =>
        resource.baseResourceId === id
          ? { ...resource, params }
          : resource
      )
    );
  };

  /**
   * Updates tags for a base resource
   * Also propagates to all assigned instances
   * 
   * @param {string} id - Resource ID
   * @param {Array} tags - Updated tags array
   */
  const handleUpdateResourceTags = (id, tags) => {
    setPoolResources((prev) =>
      prev.map((resource) =>
        resource.id === id ? { ...resource, tags } : resource
      )
    );
    
    // Also update all assigned instances
    setResources((prev) =>
      prev.map((resource) =>
        resource.baseResourceId === id
          ? { ...resource, tags }
          : resource
      )
    );
  };

  /**
   * Directly adds a resource instance to a specific project/day
   * Used by child components for quick resource creation
   * 
   * @param {string} dayId - Target day identifier
   * @param {string} projectId - Target project identifier
   * @param {string} name - Resource name
   * @param {string} role - Resource role
   */
  const handleAddResourceToProject = (dayId, projectId, name, role) => {
    const newResource = {
      id: `resource-${Date.now()}`,
      name,
      role,
      skillLevel: '',
      projectId,
      dayId,
    };
    setResources((prev) => [...prev, newResource]);
  };

  // ============================================================================
  // DATE NAVIGATION
  // ============================================================================
  
  /**
   * Navigates to the previous week (7 days earlier)
   */
  const handlePreviousWeek = () => {
    const newStartDate = new Date(currentStartDate.getTime());
    newStartDate.setDate(newStartDate.getDate() - 7);
    setCurrentStartDate(newStartDate);
    setDays(generate7Days(newStartDate));
  };

  /**
   * Navigates to the next week (7 days later)
   */
  const handleNextWeek = () => {
    const newStartDate = new Date(currentStartDate.getTime());
    newStartDate.setDate(newStartDate.getDate() + 7);
    setCurrentStartDate(newStartDate);
    setDays(generate7Days(newStartDate));
  };

  /**
   * Jumps to the current week (Monday of week containing today)
   */
  const handleToday = () => {
    const mondayOfCurrentWeek = getMonday(new Date());
    setCurrentStartDate(mondayOfCurrentWeek);
    setDays(generate7Days(mondayOfCurrentWeek));
  };

  // ============================================================================
  // DATA MANAGEMENT
  // ============================================================================
  
  /**
   * Save pool resources to Firebase
   */
  const handleSavePoolResources = async () => {
    if (!authUser || !firebase) {
      showNotification('Cannot save: User not authenticated', 'error');
      return;
    }

    try {
      showNotification('Saving pool resources to Firebase...', 'info');
      
      // Clean pool resources
      const cleanedPoolResources = poolResources.map(r => {
        const cleaned = { ...r };
        Object.keys(cleaned).forEach(key => {
          if (cleaned[key] === undefined) {
            delete cleaned[key];
          }
        });
        return cleaned;
      });
      
      // Get current state and update only poolResources
      const currentState = await firebase.doGetUserResourceManagementState(authUser.uid);
      const dataToSave = {
        ...currentState,
        poolResources: cleanedPoolResources,
      };
      
      await firebase.doSaveUserResourceManagementState(authUser.uid, dataToSave);
      
      setHasUnsavedPoolChanges(false);
      showNotification('Pool resources saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving pool resources:', error);
      showNotification(`Failed to save pool resources: ${error.message}`, 'error');
    }
  };

  /**
   * Save assignments to Firebase
   */
  const handleSaveAssignments = async () => {
    if (!authUser || !firebase) {
      showNotification('Cannot save: User not authenticated', 'error');
      return;
    }

    try {
      showNotification('Saving assignments to Firebase...', 'info');
      
      // Save only the essential assignment data (no resource properties)
      const assignmentsToSave = resources
        .filter(r => !r.isTemp)
        .map(r => ({
          id: r.id,
          baseResourceId: r.baseResourceId,
          projectId: r.projectId,
          dayId: r.dayId,
        }))
        .filter(a => a.baseResourceId && a.projectId && a.dayId); // Only save valid assignments
      
      // Get current state and update only resources
      const currentState = await firebase.doGetUserResourceManagementState(authUser.uid);
      const dataToSave = {
        ...currentState,
        resources: assignmentsToSave,
      };
      
      await firebase.doSaveUserResourceManagementState(authUser.uid, dataToSave);
      
      setHasUnsavedAssignmentChanges(false);
      showNotification('Assignments saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving assignments:', error);
      showNotification(`Failed to save assignments: ${error.message}`, 'error');
    }
  };
  
  /**
   * Clears all persisted data from Firebase and resets to initial state
   * Prompts user for confirmation before proceeding
   */
  const handleClearData = async () => {
    if (window.confirm('Are you sure you want to clear all data? This will remove all assigned resources and pool resources.')) {
      setResources([]);
      setPoolResources([]);
      showNotification('All data has been cleared.', 'success');
      
      // Clear data from Firebase
      if (authUser && firebase) {
        try {
          await firebase.userResourceManagement(authUser.uid).remove();
          setHasUnsavedPoolChanges(false);
          setHasUnsavedAssignmentChanges(false);
        } catch (error) {
          console.error('Error clearing Firebase data:', error);
          showNotification('Data cleared locally but failed to sync to Firebase.', 'warning');
        }
      }
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  // Show loading state while fetching data from Firebase
  if (loading) {
    return (
      <div className="draggit-rm-container">
        <div className="draggit-rm-loading">
          <p>Loading resource management data...</p>
        </div>
      </div>
    );
  }
  
  return (
    // Main drag-and-drop context wrapping the entire interface
    // Configures sensors (pointer, keyboard), collision detection, and drag event handlers
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
      
      {/* Main container: Two-column layout with resource pool on left, planning table on right */}
      <div className="draggit-rm-container">
        {/* Resource Pool: Left sidebar with base resources that can be cloned to projects */}
        <ResourcePool 
          resources={poolResources} 
          onAddResource={handleAddResource}
          onUpdateResource={handleUpdateResource}
          onDeleteResource={handleDeleteResource}
          onUpdateResourceParams={handleUpdateResourceParams}
          onUpdateResourceTags={handleUpdateResourceTags}
          onSaveToFirebase={handleSavePoolResources}
          hasUnsavedChanges={hasUnsavedPoolChanges}
        />
        
        {/* Planning Table Wrapper: Contains navigation and the week table */}
        <div className="draggit-rm-table-wrapper">
          {/* Navigation Controls: Week navigation and data management */}
          <div className="draggit-rm-navigation">
            {/* Left Group: Week navigation buttons */}
            <div className="draggit-rm-nav-left">
              <button onClick={handlePreviousWeek} className="draggit-rm-nav-button">
                ← Previous 7 Days
              </button>
              <button onClick={handleToday} className="draggit-rm-nav-button today">
                Today
              </button>
              <button onClick={handleNextWeek} className="draggit-rm-nav-button">
                Next 7 Days →
              </button>
            </div>
            {/* Right Group: Data management */}
            <div className="draggit-rm-nav-right">
              <button 
                onClick={handleSaveAssignments} 
                className={`draggit-rm-nav-button save-firebase ${hasUnsavedAssignmentChanges ? 'has-changes' : ''}`}
                disabled={!hasUnsavedAssignmentChanges}
              >
                {hasUnsavedAssignmentChanges ? '💾 Save Assignments *' : '✓ Assignments Saved'}
              </button>
              <button onClick={handleClearData} className="draggit-rm-nav-button clear-data">
                Clear All Data
              </button>
            </div>
          </div>
          
          {/* Table Header: Day columns */}
          <div className="draggit-rm-table-header">
            {/* First column: Project label */}
            <div className="draggit-rm-header-date-cell">Project</div>
            
            {/* Dynamic day columns */}
            {days.map((day) => {
              const dateObj = new Date(day.date);
              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const formattedDate = `${dayNames[dateObj.getDay()]}, ${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}`;
              
              return (
                <div key={day.id} className="draggit-rm-header-project-cell">
                  {formattedDate}
                </div>
              );
            })}
          </div>
          
          {/* Table Body: Each project rendered as a row with day cells */}
          <div className="draggit-rm-days-container">
            {/* Map each project (from API) to a Day component (reused as project row) */}
            {projects.length > 0 ? (
              projects.map((project) => (
                <Day
                  key={project.id}
                  id={project.id}
                  date={project.title}
                  projects={days}
                  resources={resources}
                  onAddProject={handleAddProject}
                  onAddResource={handleAddResourceToProject}
                />
              ))
            ) : (
              <div className="draggit-rm-no-projects">
                <p>Loading projects from your organizations...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DragOverlay: Shows resource card while dragging (follows cursor) */}
      <DragOverlay>
        {activeResource ? (
          <Resource
            id={activeResource.id}
            name={activeResource.name}
            role={activeResource.role}
            skillLevel={activeResource.skillLevel}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

// Export with Firebase integration and authentication
export default withEmailVerification(withAuthorization(condition)(withFirebase(DraggitRM)));